/* eslint-disable consistent-return */
import { useEffect, useContext, useRef } from 'react';
import { AuthContext } from 'src/auth/context';
import { ensureAdminConnected, getAdminSocket, adminAuthSync } from 'src/utils/socket';

import { contractBalanceUpdated } from 'src/redux/slices/contract-balance-slice';
import { fiatTransactionReceived } from 'src/redux/slices/golis-balance-slice';
import { setBankPollerStatus } from 'src/redux/slices/settings-slice';
import { pricesUpdated } from 'src/redux/slices/prices-slice';
import {
  prependAdminTransaction,
  mergeAdminTransaction,
} from 'src/redux/slices/admin-transactions-slice';
import { adminDispatch } from 'src/redux/adminStore';
import {
  mergeDashboardTransaction,
  prependDashboardTransaction,
  updateBalance,
} from 'src/redux/slices/admin-dashboard-slice';

// GLITCH FIXES (vs previous version):
// - Removed the 2s duplicate filter on updateTransaction:update — it dropped
//   real consecutive status changes for the same transaction. Reducers merge
//   by _id, so replays are harmless.
// - The old isSyncingRef guard SKIPPED the auth:sync that follows a login when
//   a guest sync was still in flight, so the admin never joined the admin room
//   and the dashboard stopped updating live. socket.js now coalesces syncs and
//   always runs the latest one.
// - Global `prices` broadcasts are now stored in the admin store too, so payout
//   dialogs can show gas in USD.
// - Listeners are attached once per socket and fan out to every mounted
//   instance's callbacks.

const attachedAdminSockets = new WeakSet();
const subscribers = new Set();

const adminAuthState = {
  isAdminAuthenticated: false,
};

function notifySubscribers(key, payload) {
  subscribers.forEach((refs) => {
    const fn = refs[key]?.current;
    if (typeof fn === 'function') {
      try {
        fn(payload);
      } catch (error) {
        console.error(`[useAdminSocket] ${key} callback failed`, error);
      }
    }
  });
}

async function syncAdminSession() {
  try {
    await ensureAdminConnected();

    const res = await adminAuthSync();

    if (!res?.ok && !res?.cancelled) {
      console.warn('❌ adminAuthSync failed — session may be invalid:', res);
    }
  } catch (error) {
    console.log('❌ admin socket sync failed:', error?.message || error);
  }
}

function attachListeners(socket) {
  if (attachedAdminSockets.has(socket)) return;
  attachedAdminSockets.add(socket);

  socket.on('contract-balances', (payload) => {
    if (!adminAuthState.isAdminAuthenticated) return;

    adminDispatch(contractBalanceUpdated(payload));
    notifySubscribers('setContractBalance', payload);
    notifySubscribers('onBalance', payload);
  });

  socket.on('prices', (prices) => {
    adminDispatch(pricesUpdated(prices));
  });

  socket.on('updateTransaction:update', (payload) => {
    const transaction = payload?.transaction;
    if (!transaction?._id) return;
    if (!adminAuthState.isAdminAuthenticated) return;

    adminDispatch(mergeAdminTransaction(transaction));
    adminDispatch(mergeDashboardTransaction(transaction));
    notifySubscribers('onTransaction', transaction);
  });

  socket.on('adminTransaction:created', (payload) => {
    const transaction = payload?.transaction;
    if (!transaction?._id) return;
    if (!adminAuthState.isAdminAuthenticated) return;

    // Both reducers upsert by _id, so a reconnect replay never duplicates rows.
    adminDispatch(prependAdminTransaction(transaction));
    adminDispatch(prependDashboardTransaction(transaction));
    notifySubscribers('onTransaction', transaction);
  });

  socket.on('golis-balance:update', (payload) => {
    if (!adminAuthState.isAdminAuthenticated) return;

    adminDispatch(fiatTransactionReceived(payload?.rows));
  });

  socket.on('bank-poller-status', (payload) => {
    if (!adminAuthState.isAdminAuthenticated) return;

    adminDispatch(
      setBankPollerStatus({
        authenticated: payload?.authenticated,
        running: payload?.running,
        activeFetch: payload?.activeFetch,
        reason: payload?.reason,
        ts: payload?.ts,
      })
    );
  });

  socket.on('fiat-balance:update', (balance) => {
    if (!adminAuthState.isAdminAuthenticated) return;

    adminDispatch(updateBalance({ fiatBalance: balance }));
  });
}

export function useAdminSocket({
  setContractBalance,
  onBalance,
  onTransaction,
  watchedTxId,
  autoConnect = true,
  autoAuthSync = true,
} = {}) {
  const { isAdminAuthenticated } = useContext(AuthContext);

  const setContractBalanceRef = useRef(setContractBalance);
  const onBalanceRef = useRef(onBalance);
  const onTransactionRef = useRef(onTransaction);
  const watchedTxIdRef = useRef(watchedTxId);

  useEffect(() => {
    setContractBalanceRef.current = setContractBalance;
    onBalanceRef.current = onBalance;
    onTransactionRef.current = onTransaction;
    watchedTxIdRef.current = watchedTxId;
  });

  useEffect(() => {
    const refs = {
      setContractBalance: setContractBalanceRef,
      onBalance: onBalanceRef,
      onTransaction: onTransactionRef,
    };

    subscribers.add(refs);

    return () => {
      subscribers.delete(refs);
    };
  }, []);

  useEffect(() => {
    const socket = getAdminSocket();
    attachListeners(socket);

    if (!autoConnect) return;

    ensureAdminConnected().catch((error) => {
      console.log('❌ admin socket connect failed:', error?.message || error);
    });
  }, [autoConnect]);

  // Login / logout → let the server re-derive the admin rooms.
  useEffect(() => {
    adminAuthState.isAdminAuthenticated = Boolean(isAdminAuthenticated);

    if (!autoAuthSync) return;

    syncAdminSession();
  }, [autoAuthSync, isAdminAuthenticated]);
}

export default useAdminSocket;
